import type { Landmark } from '../../../data/types';
import type { Ring, Vec2, Vec3 } from '../../../geo/types';
import type { LinearRGB } from '../../../core/color';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import { boxSolid, orientedFace, planPoint, prismSolid } from '../solids';
import { buildDamBuildings, damBuildingRings } from './damBuildings';
import { buildDamRailing, damRailingRings } from './damRailing';
import { DAM_DECK } from '../../../site/damDeck';
import { WORLD } from '../../../core/config';
import { buildCurveDressings } from '../../terrain/archDressings';
import { readBool } from './params';
import {
  dominantAxis,
  meshFrameOf,
  planBounds,
  planFrameOf,
  toLayout,
  type PlanFrame,
} from './planFrame';

/** Broad masonry river dam, carrying the market and connected buildings in the painted detail.
 * One large vaulted passage runs through its body. Dimensions are reconstruction estimates.
 */
export const DAM = {
  /** The masonry stands on the river bed, below the water. */
  baseY: -2.0,
  /** The city-level platform above the sluice, reached from both banks. */
  crestY: DAM_DECK.crestY,
  /** Masonry slab over the vault. */
  deckM: 0.6,
  /** The arch's crown, `crownGapM` under the deck's underside, so the spandrel closes into it. */
  crownY: 2.94,
  crownGapM: 0.06,
  /** Where the arches spring: below the water, so the opening shows from the water up. */
  springY: -0.65,
  /** How many straight faces each arch's curve is drawn with. */
  segments: 16,
  /** The pier between two openings, and the least an opening may span. */
  pierM: 2.6,
  /** How far each part runs into the one it stands against, so no two share a face. */
  sinkM: 0.05,
  /** The gate: an oak slab hanging lifted in the opening, its lower edge this far over the water,
   *  clear of a rowing boat and its crew (BOAT.eyeHeight and headroom, with the waves' crests). */
  gate: { thickM: 0.3, liftedM: 2.3 },
  /** The single large tunnel drawn in the supplied detail. */
  sluiceSpanM: 12.0,
  /** How far west of the dam's middle the tunnel runs: beside the tall houses on the Damrak head. */
  sluiceWestM: 5.75,
} as const;

/** The bank's paving, level with the Dam at either end: the ground the streets and squares lie on. */
export const DAM_BANK_Y = DAM_DECK.crestY;

export interface DamColors {
  /** The dam's stone. */
  readonly wall: LinearRGB;
  /** The paved crest and bank approaches. */
  readonly pavement: LinearRGB;
  /** The gates in the openings. */
  readonly gate: LinearRGB;
  /** Connected civic buildings on the crest. */
  readonly house: LinearRGB;
  readonly slate: LinearRGB;
  readonly roof: LinearRGB;
  readonly opening: LinearRGB;
  /** The lighter stone of the railing's coping and finials. */
  readonly trim: LinearRGB;
}

interface DamPlan {
  readonly plan: PlanFrame;
  readonly frame: Frame;
  readonly u0: number;
  readonly u1: number;
  readonly v0: number;
  readonly v1: number;
  /** The openings' ends along the dam, in order from `u0`'s side. */
  readonly openings: readonly (readonly [number, number])[];
}

/** One sluice under the platform, with solid abutments and level bank approaches. */
function damPlan(landmark: Landmark, warn: (msg: string) => void): DamPlan {
  const plan = planFrameOf(landmark.footprint, dominantAxis(landmark.footprint, landmark.heading));
  const frame = meshFrameOf(plan);
  const r = planBounds(plan);
  const length = r.u1 - r.u0;
  const halfSpan = Math.min(DAM.sluiceSpanM / 2, (length - 2 * DAM.pierM) / 2);
  if (halfSpan <= 0) warn(`${landmark.id}: insufficient room for the sluice`);
  const room = Math.max(0, length / 2 - DAM.pierM - Math.max(0.5, halfSpan));
  const centre = (r.u0 + r.u1) / 2 - Math.min(DAM.sluiceWestM, room);
  const openings: [number, number][] = [
    [centre - Math.max(0.5, halfSpan), centre + Math.max(0.5, halfSpan)],
  ];

  return { plan, frame, u0: r.u0, u1: r.u1, v0: r.v0, v1: r.v1, openings };
}

/** A sluice passage under a dam: the middle line of its water from one face to the other, in
 *  layout coordinates, and half its clear span at the springing. */
export interface DamPassage {
  readonly from: Vec2;
  readonly to: Vec2;
  readonly halfSpanM: number;
}

export function damPassages(landmark: Landmark): readonly DamPassage[] {
  const dam = damPlan(landmark, () => undefined);
  return dam.openings.map(([a, b]) => ({
    from: toLayout(dam.plan, [(a + b) / 2, dam.v0]),
    to: toLayout(dam.plan, [(a + b) / 2, dam.v1]),
    halfSpanM: (b - a) / 2,
  }));
}

/** The arch's intrados at `u`, measured from the middle of its opening. */
function archY(halfSpan: number, u: number): number {
  const t = u / halfSpan;
  return DAM.crownY - (DAM.crownY - DAM.springY) * t * t;
}

/** The intrados' stations, from the springing on one pier over the crown to the other. */
function archStations(halfSpan: number): number[] {
  const n = DAM.segments;
  return Array.from({ length: n + 1 }, (_, i) => halfSpan - (2 * halfSpan * i) / n);
}

/**
 * The dam: the piers, the spandrels that carry the crest over the openings, the deck with the
 * square's paving on it, the gates hanging in the openings and the houses standing on the crest over
 * them, at the water's edge.
 *
 * The parts meet where a masonry dam's parts meet, and the audit reads two solids that share a face
 * as one body with an inward wall, so each part runs `SINK` m into the one it stands against: no two
 * faces lie in a plane and no two parts share a vertex.
 */
export function buildDam(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: DamColors,
  ids: { readonly edgeId: number; readonly seed: number },
  warn: (msg: string) => void
): void {
  const dam = damPlan(landmark, warn);
  const frame = dam.frame;
  const gates = readBool(landmark.params, 'gates', true, warn);
  const { v0, v1 } = dam;
  const width = v1 - v0;
  const base = DAM.baseY;
  const sink = DAM.sinkM;
  const deckBottom = DAM.crownY + DAM.crownGapM;
  const base0 = { ...ids, strokeAngle: 0, extent: [width, DAM.crestY - base] as const };
  const wall: VertexStyle = { ...base0, color: colors.wall, surface: SURFACE.stone };
  const crest: VertexStyle = {
    ...base0,
    color: colors.pavement,
    surface: SURFACE.street,
    extent: [width, DAM.deckM] as const,
  };
  const houseWall: VertexStyle = {
    ...base0,
    color: colors.house,
    surface: SURFACE.brick,
    extent: [13, 10] as const,
  };
  const houseRoof: VertexStyle = {
    ...base0,
    color: colors.roof,
    surface: SURFACE.roofTile,
    extent: [23, 13] as const,
  };
  const window: VertexStyle = {
    ...base0,
    color: colors.opening,
    surface: SURFACE.linework,
    extent: [1.2, 1.65] as const,
  };
  const plank: VertexStyle = {
    ...base0,
    color: colors.gate,
    surface: SURFACE.wood,
    extent: [DAM.gate.thickM, DAM.crestY - base] as const,
  };

  /* The piers between the openings, from the river bed to `sink` into the deck and `sink` into the
     arches' own ends on either side, so an arch is a separate body however they meet. The end piers
     run back to the dam's own ends, where the banks take the crossing. */
  for (let i = 0; i <= dam.openings.length; i++) {
    const u0 = i === 0 ? dam.u0 : dam.openings[i - 1]![1] - sink;
    const u1 = i === dam.openings.length ? dam.u1 : dam.openings[i]![0] + sink;
    prismSolid(
      builder,
      { ...wall, extent: [u1 - u0, deckBottom - base] as const },
      frame,
      [
        [u0, v0],
        [u1, v0],
        [u1, v1],
        [u0, v1],
      ],
      base,
      deckBottom + sink,
      { top: true, bottom: true }
    );
  }

  /* The spandrels over the openings: the intrados the river ran under, closed by a straight extrados
     at the deck's underside, so the wall over an arch is the deck's own bearing. */
  const at = (p: Vec2, v: number): Vec3 => planPoint(p[0], p[1], v);
  /* The curve rises from the springing to the crown and back down, so its tangent always has a
     positive y and the intrados' outward normal is that tangent turned a quarter turn: the material
     of the wall lies over the intrados, never under it. */
  const intradosNormal = (a: Vec2, b: Vec2): Vec2 => [-(b[1] - a[1]), b[0] - a[0]];
  for (const [from, to] of dam.openings) {
    const centre = (from + to) / 2;
    const halfSpan = (to - from) / 2;
    const stations = archStations(halfSpan).map((u) => centre + u);
    const inner = stations.map((u): Vec2 => [u, archY(halfSpan, u - centre)]);
    buildCurveDressings(builder, wall, frame, [...inner].reverse(), width);
    const outer = stations.map((u): Vec2 => [u, deckBottom + sink]);
    for (let s = 0; s + 1 < stations.length; s++) {
      const a = inner[s]!;
      const b = inner[s + 1]!;
      const c = outer[s + 1]!;
      const d = outer[s]!;
      /* The spandrel's two faces, at v0 and at v1, and the intrados between them. Its extrados lies
         inside the deck's slab, whose underside closes it. */
      orientedFace(builder, wall, frame, [at(a, v0), at(b, v0), at(c, v0), at(d, v0)], [0, 0, 1]);
      orientedFace(builder, wall, frame, [at(a, v1), at(b, v1), at(c, v1), at(d, v1)], [0, 0, -1]);
      const inward = intradosNormal(a, b);
      const tunnel: VertexStyle = {
        ...wall,
        color: [colors.wall[0] * 0.3, colors.wall[1] * 0.3, colors.wall[2] * 0.3],
      };
      orientedFace(
        builder,
        tunnel,
        frame,
        [at(a, v0), at(b, v0), at(b, v1), at(a, v1)],
        [inward[0], inward[1], 0]
      );
      /* The extrados closes the spandrel's top, `sink` into the deck's slab. */
      orientedFace(builder, wall, frame, [at(d, v0), at(c, v0), at(c, v1), at(d, v1)], [0, 1, 0]);
    }
    /* The opening's ends, running `sink` into the piers and the abutments. */
    for (const [index, along] of [
      [0, 1],
      [stations.length - 1, -1],
    ] as const) {
      orientedFace(
        builder,
        wall,
        frame,
        [
          at(inner[index]!, v0),
          at(outer[index]!, v0),
          at(outer[index]!, v1),
          at(inner[index]!, v1),
        ],
        [along, 0, 0]
      );
    }
  }

  /* The broad crest over the vault and abutments. */
  prismSolid(
    builder,
    crest,
    frame,
    [
      [dam.u0, v0],
      [dam.u1, v0],
      [dam.u1, v1],
      [dam.u0, v1],
    ],
    deckBottom,
    DAM.crestY,
    { top: true, bottom: true }
  );

  buildDamRailing(
    builder,
    { stone: wall, trim: { ...wall, color: colors.trim } },
    frame,
    dam,
    DAM.crestY
  );

  buildDamBuildings(
    builder,
    {
      wall: houseWall,
      roof: houseRoof,
      slate: { ...houseRoof, color: colors.slate, surface: SURFACE.roofSlate },
      window,
      trim: { ...wall, extent: [13, 0.2] },
    },
    frame,
    DAM.crestY
  );

  /* The gate: an oak slab hanging lifted in the middle of the passage, its upper edge `sink` into
     the crest. It hung with its lower edge just over the water until 6 October 2026, which shut
     the Damrak off from the Rokin; the user asked for the passage to be rowed. That the sluice
     stood open to boats, and how its gate was drawn up, are estimates. */
  if (!gates) return;
  const gateBottom = WORLD.waterY + DAM.gate.liftedM;
  const middle = (v0 + v1) / 2;
  for (const [from, to] of dam.openings) {
    boxSolid(
      builder,
      plank,
      frame,
      [from + sink, gateBottom, middle - DAM.gate.thickM / 2],
      [to - sink, deckBottom + sink, middle + DAM.gate.thickM / 2],
      { top: true, bottom: true }
    );
  }
}

/**
 * The houses and the railing along the two water faces block the crest; the market and the bank
 * approaches remain walkable.
 */
export function damObstacles(landmark: Landmark): readonly Ring[] {
  const dam = damPlan(landmark, () => undefined);
  return [...damBuildingRings(dam.plan), ...damRailingRings(landmark)];
}
